import RatingStar from "@/components/app/RatingStar";
import { SiteInfo } from "@/globals";
import{ convertCurrencySign, convertPromotionCodeValue, formatSalesQuantity } from "@/js/format/Format";
import { getProductById, getProducts, ProductResponse } from "@/js/repository/ProductRepository";
import { getCategoriesByProducts } from "@/js/repository/CategoryRepository";
import { Separator } from "@radix-ui/react-separator";
import Head from "next/head";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { ProductList } from "./products";

const truncateTitle = (title: string, maxLength: number = 70) => {
  return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
};

const getFullTitle = (productTitle: string, discountRate: number, siteName: string) => {
  const maxTitleLength = 70;
  const discountRateText = discountRate > 0 ? `[${discountRate.toFixed(0)}% OFF] ` : '';
  return `${truncateTitle(`${discountRateText}${productTitle} | ${siteName}`, maxTitleLength)}`;
};

const TabNavigation = ({ currentSection, onTabClick }: { currentSection: string, onTabClick: (section: string) => void }) => {
  const { t } = useTranslation();
  
  return (
    <div className="flex space-x-8 border-b">
      <button 
        className={`py-4 font-medium ${currentSection === 'description' ? 'text-[#fd384f] border-b-2 border-[#fd384f]' : 'text-gray-500'}`}
        onClick={() => onTabClick('description')}
      >
        {t("productDescription")}
      </button>
      <button 
        className={`py-4 font-medium ${currentSection === 'review' ? 'text-[#fd384f] border-b-2 border-[#fd384f]' : 'text-gray-500'}`}
        onClick={() => onTabClick('review')}
      >
        {t("reviews")}
      </button>
      <button 
        className={`py-4 font-medium ${currentSection === 'related' ? 'text-[#fd384f] border-b-2 border-[#fd384f]' : 'text-gray-500'}`}
        onClick={() => onTabClick('related')}
      >
        {t("relatedItems")}
      </button>
    </div>
  );
};

export async function getServerSideProps(context: any) {
  const { locale } = context;
  const { id } = context.query;
  const siteUrl = `https://${context.req.headers.host}`;

  const categories = await getCategoriesByProducts(locale);
  const product = await getProductById(locale, id);

  if(!product) {
    return {
      notFound: true,
    }
  }

  const relatedProducts = await getProducts(locale, 1, 8, product.detail.second_level_category_id, product.detail.product_id);

  if(product.hidden) {
    context.res.statusCode = 410;
  }

  // 서버 사이드에서 날짜 포맷팅
  const formattedLastmod = new Date(product.lastmod).toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return {
    props: { categories, product, relatedProducts, siteUrl, formattedLastmod },
  }
}

export default function Products(props: any) {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const siteUrl = props.siteUrl;
  const descriptionRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  const relatedRef = useRef<HTMLDivElement>(null);
  const [showFixedButton, setShowFixedButton] = useState(false);
  const footerRef = useRef<HTMLDivElement>(null);
  const isHidden = props.product.hidden;

  useEffect(() => {
    const handleScroll = () => {
      if (descriptionRef.current) {
        const descriptionTop = descriptionRef.current.getBoundingClientRect().top;
        const footerElement = document.querySelector('footer');
        const footerTop = footerElement ? footerElement.getBoundingClientRect().top : window.innerHeight;
        
        // description이 보이고 footer가 아직 화면에 들어오지 않았을 때만 버튼 표시
        setShowFixedButton(descriptionTop < 0 && footerTop > window.innerHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (section: string) => {
    const headerHeight = 64; // 헤더 높이 (px)
    let targetRef: React.RefObject<HTMLDivElement | null> | null = null;

    switch(section) {
      case 'description':
        targetRef = descriptionRef;
        break;
      case 'review':
        targetRef = reviewRef;
        break;
      case 'related':
        targetRef = relatedRef;
        break;
    }

    if (targetRef?.current) {
      const elementPosition = targetRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const product: ProductResponse = props.product;
  const sale_price = parseFloat(product.detail.target_sale_price);
  const original_price = parseFloat(product.detail.target_original_price);
  const discount_rate = (original_price - sale_price) / original_price * 100;
  const rating = product.detail.evaluate_rate ? (parseInt(product.detail.evaluate_rate.replace('%', ''))/20).toFixed(1) : null;
  const description = `${convertCurrencySign(product.detail.target_sale_price_currency, sale_price.toLocaleString(locale))} | ${ discount_rate.toFixed(0) }% ${t("discount")} | ${ formatSalesQuantity(product.detail.lastest_volume, locale) } ${t("sold")} | ${t("averageReviewRating")}: ★${rating} | ${product.detail.first_level_category_name}, ${product.detail.second_level_category_name} | ${t("buyOnAliExpress")}`;

  const openProductLink = (product: ProductResponse, isViewMore: boolean = false) => {
    window.open(`${product.detail.promotion_link}`);

    // Google Analytics
    if (typeof window !== 'undefined') {
      const gtag = (window as any).gtag;
      if(!gtag) return;

      gtag('event', isViewMore ? '구매버튼 클릭 (더보기)' : '구매버튼 클릭', {
        'product_title': product.detail.product_title,
        'product_id': product.detail.product_id
      });
    }
  }

  return (
    <>
      <Head>
        <title>{getFullTitle(product.detail.product_title, discount_rate, SiteInfo.siteName)}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${siteUrl}/product?id=${product.detail.product_id}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`${siteUrl}/product?id=${product.detail.product_id}`} />
        <meta property="og:title" content={getFullTitle(product.detail.product_title, discount_rate, SiteInfo.siteName)} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={product.detail.product_main_image_url} />
        <meta property="og:site_name" content={SiteInfo.siteName} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getFullTitle(product.detail.product_title, discount_rate, SiteInfo.siteName)} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={product.detail.product_main_image_url} />

        <script type="application/ld+json" dangerouslySetInnerHTML={
          {
            __html: JSON.stringify(
            {
              "@context": "https://schema.org/",
              "@type": "Product",
              "name": truncateTitle(product.detail.product_title, 70 - (SiteInfo.siteName.length + 3)),
              "image": [
                product.detail.product_main_image_url,
                ...(product.detail.product_small_image_urls?.string || []).filter((_, index) => index > 0)
              ],
              "description": description,
              "category": `${product.detail.first_level_category_name} > ${product.detail.second_level_category_name}`,
              "sku": product.detail.product_id,
              "offers": {
                "@type": "Offer",
                "priceCurrency": product.detail.target_sale_price_currency,
                "price": sale_price,
                "itemCondition": "https://schema.org/NewCondition",
                "availability": "https://schema.org/InStock",
                "url": `${siteUrl}/product?id=${product.detail.product_id}`,
                "priceSpecification": {
                  "@type": "PriceSpecification",
                  "price": sale_price,
                  "priceCurrency": product.detail.target_sale_price_currency,
                  "valueAddedTaxIncluded": true,
                  "priceType": "https://schema.org/SalePrice"
                }
              }
            }, null, 2)
          }
        }>
        </script>
      </Head>

      {/* breadcrumb */}
      <div className="text-medium font-medium mb-4">
        <span className="underline"><a href={`/products?category=${product.detail.first_level_category_id}`}>{ product.detail.first_level_category_name }</a></span><span className="mx-1">＞</span><span className="underline"><a href={`/products?category=${product.detail.second_level_category_id}`}>{ product.detail.second_level_category_name }</a></span>
        <div className="text-sm text-gray-500 mt-2">
          {t("lastUpdated")}: {props.formattedLastmod}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row align-top">
        <img className="w-full sm:min-w-[calc(50%-24px)] sm:mr-[24px] mb-auto align-top object-scale-down aspect-square bg-gray-100 rounded-xl" src={product.detail.product_main_image_url} alt={product.detail.product_main_image_url} width={800} height={800} loading="lazy"/>

        <div className="w-full mt-4 sm:mt-0">
          <h1 className="text-[1.2em] font-bold break-all">{product.detail.product_title}</h1>

          <div className="text-[#fd384f] font-bold text-[1.2em]">
            {convertCurrencySign(product.detail.target_sale_price_currency, sale_price.toLocaleString(locale))}
            <span className="ml-2 mr-4 line-through font-medium text-[0.8em] text-gray-500">{convertCurrencySign(product.detail.target_sale_price_currency, original_price.toLocaleString(locale))}</span>
            <span className="px-2 py-1 align-middle my-auto text-[#fd384f] text-[0.7em] bg-[#ffe6e7] rounded">-{ discount_rate.toFixed(0) }%</span>
          </div>
              
          <div className="mt-2 mb-4 flex">
            { rating &&
              <RatingStar rating={ Number(rating) } />
            }
            <span className="ml-1">{rating}</span>
            <Separator className="h-[12px] align-middle my-auto mx-2 border-l border-gray-300"/>
            <span>{ formatSalesQuantity(product.detail.lastest_volume, locale) } {t("sold")}</span>
          </div>

          { !isHidden &&
            <button className="w-full h-[44px] bg-[#FD384F] text-white rounded-lg text-[1.2em] mt-3" onClick={() => openProductLink(product)} title={t("buyOnAliExpress")}>{t("buyOnAliExpress")}</button>
          }
          { isHidden &&
            <div className="mt-4 text-red-500 text-xl font-bold">
              {t("productHidden")}
            </div>
          }

          { product.detail.promo_code_info &&
            <div className="mt-8">
              <div className="text-lg font-bold">{t("promoCode")}</div>
              <div className="text-sm mt-1 bg-gray-200 rounded-md p-3">
                <div>
                  <span className="text-base font-bold align-middle my-auto underline">{product.detail.promo_code_info.promo_code}</span>
                  <button className="ml-3 text-md align-middle my-auto text-red-500" onClick={() => {
                    navigator.clipboard.writeText(product.detail.promo_code_info.promo_code);
                    alert(t("copiedPromoCodeMessage"));
                  }} title={t("copy")}>{t("copy")}</button>
                </div>
                <div className="mt-1">{convertPromotionCodeValue(product.detail.promo_code_info.code_value)}</div>
                {/* <div className="mt-1">{product.detail.promo_code_info.code_value}</div> */}
                <div className="mt-1">{t("expires")} {new Date(product.detail.promo_code_info.code_availabletime_end).toLocaleString(locale)}</div>
              </div>
              
            </div>
          }
        </div>
      </div>

      {/* Fixed Buy Button */}
      {showFixedButton && !isHidden && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/10 backdrop-blur-sm border-t border-gray-200/50 py-2 px-4 z-50 shadow-lg">
          <div className="max-w-7xl mx-auto">
            <button 
              className="w-full h-[40px] bg-[#FD384F] text-white rounded-lg text-[1.2em]" 
              onClick={() => openProductLink(product)} 
              title={t("buyOnAliExpress")}
            >
              {t("buyOnAliExpress")}
            </button>
          </div>
        </div>
      )}

      <Separator className="my-12 border-t" />

      {/* Description Section */}
      <div ref={descriptionRef} className="pt-8">
        <div className="mb-8">
          <TabNavigation currentSection="description" onTabClick={scrollToSection} />
        </div>
        { product.detail.product_video_url && 
          <video width="100%" className="mt-2 mb-24 max-h-[693px]" controls>
            <source src={product.detail.product_video_url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        }

        <div className="mt-2">
          { product.detail.product_small_image_urls?.string?.map((image: string, index: number) => {
            if(index === 0) return;

            return (
                <img src={image} alt={image} width={800} height={800} className="w-full" key={`image-${index}`} loading="lazy"/>
            );
          }) }
        </div>

        { !isHidden &&
          <div className="text-center mt-12">
            <button className="bg-[#e2e2e2] px-14 py-3 rounded-3xl text-md font-bold" onClick={() => openProductLink(product, true)} title={t("viewMore")}>{t("viewMore")}</button>
          </div>
        }
      </div>

      {/* Review Section */}
      <div ref={reviewRef} className="pt-8">
        <div className="mb-8">
          <TabNavigation currentSection="review" onTabClick={scrollToSection} />
        </div>
        <div className="text-center py-12">
          <div className="flex items-center justify-center mb-6">
            { rating && (
              <>
                <RatingStar rating={Number(rating)} />
                <span className="ml-2 text-lg font-medium">{rating}</span>
                <Separator className="h-[16px] align-middle my-auto mx-2 border-l border-gray-300"/>
                <span className="text-gray-500">{formatSalesQuantity(product.detail.lastest_volume, locale)} {t("sold")}</span>
              </>
            )}
          </div>
          { !isHidden &&
            <button 
              className="bg-[#fd384f] text-white px-8 py-3 rounded-3xl text-md font-bold hover:bg-[#e62e44] transition-colors"
              onClick={() => window.open(`${product.detail.promotion_link}#tab-reviews`, '_blank')}
            >
              {t("checkReviewsOnAliExpress")}
            </button>
          }
        </div>
      </div>

      {/* Related Items Section */}
      <div ref={relatedRef} className="pt-8">
        <div className="mb-8">
          <TabNavigation currentSection="related" onTabClick={scrollToSection} />
        </div>

        <ProductList products={props.relatedProducts} />
      </div>
    </>
  );
}